import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import {
  ArrowLeft, Package, Spinner, FloppyDisk, CheckCircle, Warning,
  MagnifyingGlass, ArrowClockwise, ArrowSquareOut, DeviceMobile,
} from "@phosphor-icons/react";

/** Format a price number to `$12.34`. */
const fmt = (n) => {
  const v = Number(n);
  if (!isFinite(v) || v <= 0) return "—";
  return `$${v.toFixed(2)}`;
};

/** Last-modified date helper — falls back cleanly when empty. */
const fmtDate = (s) => {
  if (!s) return "—";
  const d = new Date(s);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

export default function InventoryPage() {
  const [rows, setRows] = useState([]);        // original server rows
  const [edits, setEdits] = useState({});      // listing_id -> new label
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [resultSummary, setResultSummary] = useState(null); // {updated, failed, errors:[{listing_id,error}]}

  const load = async () => {
    setLoading(true);
    setResultSummary(null);
    try {
      const { data } = await api.get("/ebay/inventory");
      setRows(Array.isArray(data) ? data : []);
      setEdits({});
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setRows([]);
      setResultSummary({ updated: 0, failed: 0, errors: [], fatal: detail || "Failed to load inventory" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Pending changes: label is non-empty and differs from the server value.
  const dirty = useMemo(() => {
    const out = [];
    for (const r of rows) {
      const next = edits[r.listing_id];
      if (next === undefined) continue;
      const current = r.custom_label || "";
      if (next.trim() !== current.trim()) out.push({ listing_id: r.listing_id, custom_label: next.trim() });
    }
    return out;
  }, [rows, edits]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      (r.title || "").toLowerCase().includes(q) ||
      (r.brand || "").toLowerCase().includes(q) ||
      (r.listing_id || "").toLowerCase().includes(q) ||
      (r.custom_label || "").toLowerCase().includes(q) ||
      (edits[r.listing_id] || "").toLowerCase().includes(q)
    );
  }, [rows, edits, search]);

  const onChange = (listing_id, v) => {
    setEdits((prev) => ({ ...prev, [listing_id]: v }));
  };

  const save = async () => {
    if (dirty.length === 0) return;
    setSaving(true);
    setResultSummary(null);
    try {
      const { data } = await api.post("/ebay/bulk-update-labels", { updates: dirty });
      const errors = (data.results || []).filter((r) => !r.success).map((r) => ({ listing_id: r.listing_id, error: r.error }));
      setResultSummary({ updated: data.updated || 0, failed: data.failed || 0, errors });
      // On success, merge saved labels into rows + clear those edits
      if ((data.updated || 0) > 0) {
        const savedIds = new Set(
          (data.results || []).filter((r) => r.success).map((r) => r.listing_id)
        );
        const byId = new Map(dirty.map((d) => [d.listing_id, d.custom_label]));
        setRows((prev) => prev.map((r) =>
          savedIds.has(r.listing_id)
            ? { ...r, custom_label: byId.get(r.listing_id) ?? r.custom_label, last_modified: new Date().toISOString() }
            : r
        ));
        setEdits((prev) => {
          const next = { ...prev };
          for (const id of savedIds) delete next[id];
          return next;
        });
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Save failed";
      setResultSummary({ updated: 0, failed: dirty.length, errors: [], fatal: detail });
    } finally {
      setSaving(false);
    }
  };

  // Mobile guard — the table flow is desktop-only per product spec.
  return (
    <div className="min-h-screen bg-white">
      {/* Mobile notice */}
      <div className="md:hidden min-h-screen flex flex-col items-center justify-center px-6 text-center" data-testid="inventory-mobile-guard">
        <DeviceMobile size={48} weight="bold" className="text-neutral-400 mb-3" />
        <h1 className="font-heading text-2xl font-black uppercase tracking-tighter mb-2">Open On Desktop</h1>
        <p className="font-body text-sm text-neutral-500 max-w-xs">
          The Inventory Manager is a desktop-only tool. Please open GrabOrSkip on a laptop or desktop to bulk-edit your eBay custom labels.
        </p>
        <Link to="/history" className="mt-6 border-2 border-black px-4 py-2 font-heading text-xs font-bold uppercase active:translate-y-[1px]">
          Back to History
        </Link>
      </div>

      {/* Desktop page */}
      <div className="hidden md:block">
        {/* Header */}
        <div className="sticky top-0 z-30 bg-white border-b-4 border-black">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Link to="/history" className="border-2 border-black p-2 active:translate-y-[1px]" data-testid="inventory-back-btn">
                <ArrowLeft size={20} weight="bold" />
              </Link>
              <Package size={24} weight="bold" />
              <h1 className="font-heading text-3xl font-black uppercase tracking-tighter">Inventory Manager</h1>
              <span className="ml-2 font-body text-xs text-neutral-500" data-testid="inventory-count">
                {loading ? "…" : `${rows.length} active listing${rows.length === 1 ? "" : "s"}`}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <MagnifyingGlass size={14} weight="bold" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search title / brand / SKU…"
                  className="pl-8 pr-3 py-2 border-2 border-black font-body text-sm w-80"
                  data-testid="inventory-search"
                />
              </div>
              <button
                onClick={load}
                disabled={loading}
                className="border-2 border-black px-3 py-2 font-heading text-xs font-bold uppercase flex items-center gap-1.5 hover:bg-neutral-50 active:translate-y-[1px] disabled:opacity-50"
                data-testid="inventory-refresh-btn"
                title="Refresh"
              >
                <ArrowClockwise size={14} weight="bold" className={loading ? "animate-spin" : ""} /> Refresh
              </button>
              <button
                onClick={save}
                disabled={saving || dirty.length === 0}
                className="border-2 border-black bg-black text-white px-4 py-2 font-heading text-xs font-bold uppercase flex items-center gap-2 active:translate-y-[1px] disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="inventory-save-btn"
              >
                {saving ? <Spinner size={14} weight="bold" className="animate-spin" /> : <FloppyDisk size={14} weight="bold" />}
                {saving ? "Saving…" : `Save ${dirty.length || ""} ${dirty.length === 1 ? "change" : "changes"}`.trim()}
              </button>
            </div>
          </div>

          {/* Result banner */}
          {resultSummary && (
            <div
              className={`max-w-7xl mx-auto px-6 py-2 border-t-2 border-black font-body text-sm ${
                resultSummary.fatal || resultSummary.failed > 0 ? "bg-red-50" : "bg-green-50"
              }`}
              data-testid="inventory-result-banner"
            >
              {resultSummary.fatal ? (
                <span className="flex items-center gap-2 text-red-700">
                  <Warning size={16} weight="bold" /> {resultSummary.fatal}
                </span>
              ) : (
                <span className="flex items-center gap-3 flex-wrap">
                  {resultSummary.updated > 0 && (
                    <span className="flex items-center gap-1 text-green-700">
                      <CheckCircle size={16} weight="bold" /> Updated {resultSummary.updated} listing{resultSummary.updated === 1 ? "" : "s"} on eBay.
                    </span>
                  )}
                  {resultSummary.failed > 0 && (
                    <span className="flex items-center gap-1 text-red-700">
                      <Warning size={16} weight="bold" /> {resultSummary.failed} failed.
                    </span>
                  )}
                </span>
              )}
              {resultSummary.errors && resultSummary.errors.length > 0 && (
                <ul className="mt-1 text-xs text-red-700 list-disc list-inside">
                  {resultSummary.errors.slice(0, 5).map((e) => (
                    <li key={e.listing_id}>
                      <span className="font-mono">{e.listing_id}</span>: {e.error}
                    </li>
                  ))}
                  {resultSummary.errors.length > 5 && <li>+{resultSummary.errors.length - 5} more…</li>}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* Table */}
        <div className="max-w-7xl mx-auto px-6 py-6">
          {loading ? (
            <div className="p-12 text-center">
              <Spinner size={28} weight="bold" className="animate-spin mx-auto text-neutral-400" />
              <div className="mt-3 font-body text-sm text-neutral-500">Loading inventory from eBay…</div>
            </div>
          ) : rows.length === 0 ? (
            <div className="p-12 text-center border-2 border-dashed border-neutral-300" data-testid="inventory-empty">
              <Package size={48} weight="bold" className="mx-auto text-neutral-300 mb-3" />
              <div className="font-heading text-lg font-bold uppercase tracking-tight">No Active Listings</div>
              <p className="font-body text-sm text-neutral-500 mt-2 max-w-md mx-auto">
                Once you list items to eBay from GrabOrSkip, they'll appear here and you can bulk-assign storage bin / shelf labels.
              </p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center font-body text-sm text-neutral-500" data-testid="inventory-no-results">
              No listings match your search.
            </div>
          ) : (
            <div className="border-4 border-black overflow-x-auto" data-testid="inventory-table">
              <table className="w-full text-sm">
                <thead className="bg-black text-white font-heading uppercase text-[11px] tracking-wide">
                  <tr>
                    <th className="text-left px-3 py-2 w-14">Img</th>
                    <th className="text-left px-3 py-2">Title</th>
                    <th className="text-left px-3 py-2 w-36">Brand</th>
                    <th className="text-right px-3 py-2 w-24">Price</th>
                    <th className="text-left px-3 py-2 w-32">Listing ID</th>
                    <th className="text-left px-3 py-2 w-64">Custom Label (SKU)</th>
                    <th className="text-left px-3 py-2 w-28">Modified</th>
                    <th className="text-right px-3 py-2 w-16"> </th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r, idx) => {
                    const editValue = edits[r.listing_id];
                    const current = r.custom_label || "";
                    const isDirty = editValue !== undefined && editValue.trim() !== current.trim();
                    return (
                      <tr
                        key={r.listing_id}
                        className={`border-t-2 border-black ${idx % 2 === 1 ? "bg-neutral-50" : "bg-white"} ${isDirty ? "bg-yellow-50" : ""}`}
                        data-testid={`inventory-row-${r.listing_id}`}
                      >
                        <td className="px-3 py-2">
                          <div className="w-10 h-10 border-2 border-black bg-neutral-100 overflow-hidden">
                            {r.thumbnail ? (
                              <img src={`data:image/jpeg;base64,${r.thumbnail}`} alt="" className="w-full h-full object-cover" />
                            ) : null}
                          </div>
                        </td>
                        <td className="px-3 py-2 max-w-[420px]">
                          <div className="font-body text-sm truncate" title={r.title}>{r.title}</div>
                        </td>
                        <td className="px-3 py-2 font-body text-xs text-neutral-600 truncate">{r.brand || "—"}</td>
                        <td className="px-3 py-2 text-right font-heading font-bold tracking-tight">{fmt(r.price)}</td>
                        <td className="px-3 py-2 font-mono text-[11px] text-neutral-500">{r.listing_id}</td>
                        <td className="px-3 py-2">
                          <input
                            type="text"
                            value={editValue !== undefined ? editValue : current}
                            onChange={(e) => onChange(r.listing_id, e.target.value)}
                            placeholder="e.g. BIN-A-12"
                            maxLength={50}
                            className={`w-full border-2 px-2 py-1.5 font-mono text-xs focus:outline-none ${isDirty ? "border-yellow-500 bg-yellow-50" : "border-neutral-300 focus:border-black"}`}
                            data-testid={`inventory-input-${r.listing_id}`}
                          />
                        </td>
                        <td className="px-3 py-2 font-body text-[11px] text-neutral-500">{fmtDate(r.last_modified)}</td>
                        <td className="px-3 py-2 text-right">
                          {r.ebay_url ? (
                            <a
                              href={r.ebay_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-neutral-500 hover:text-black"
                              title="View on eBay"
                              data-testid={`inventory-link-${r.listing_id}`}
                            >
                              <ArrowSquareOut size={14} weight="bold" />
                            </a>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <p className="mt-4 font-body text-[11px] text-neutral-500 max-w-3xl">
            Tip: Custom Label (a.k.a. SKU) is private to you on eBay — use it to encode a storage location like <span className="font-mono">BIN-A-12</span> or <span className="font-mono">RACK-3/SHELF-2</span>. Changes are pushed to eBay via ReviseItem when you click Save.
          </p>
        </div>
      </div>
    </div>
  );
}
